import React, { useState, useEffect, useCallback } from 'react';
import notify from './utils/notify';
import { useApi } from './useApi';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import BottomNav from './BottomNav';
import { FiBarChart2, FiGrid, FiTrendingUp, FiSettings, FiUser, FiUsers, FiShield, FiSearch, FiInfo, FiLayers, FiActivity, FiLogIn, FiLogOut } from 'react-icons/fi';
import './AdminDashboard.css';
import PageHeader from './components/PageHeader';
import AuditLogList from './components/audit/AuditLogList';
import AdminInsights from './components/admin/AdminInsights';
import UserTimeline from './components/admin/UserTimeline';

const AdminDashboard = () => {
    const { authenticatedFetch } = useApi();
    const {
        userProfile, isProfileLoading,
        adminSociety, enterSocietyAsAdmin, exitAdminSociety,
    } = useAuth();
    const navigate = useNavigate();

    /* Open a society without joining it. A support account that JOINED every
       society it had to look at would appear on tee sheets, in member lists and
       in the Order of Merit — so the membership stays absent and the server
       (society_required) admits a system admin on the strength of the flag. */
    const openSociety = (society) => {
        enterSocietyAsAdmin(society);
        navigate('/');
    };
    
    const [view, setView] = useState('users'); // 'users' | 'societies' | 'audit' | 'insights'
    const [timelineFor, setTimelineFor] = useState(null); // userId whose activity is open
    const [users, setUsers] = useState([]);
    const [societies, setSocieties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [adminCount, setAdminCount] = useState(0);
    const [error, setError] = useState('');
    const [editingMembership, setEditingMembership] = useState(null); // { id, societyId, currentId }
    const [societyPlayers, setSocietyPlayers] = useState([]);
    const [savingLink, setSavingLink] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null); // userId being edited
    const [planForm, setPlanForm] = useState({ plan_type: 'free', plan_expiry_date: '' });
    const [savingPlan, setSavingPlan] = useState(false);

    const PER_PAGE = 20;

    // Debounce the search term so we don't hammer the server on every keystroke
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
        return () => clearTimeout(id);
    }, [searchTerm]);

    // Reset to page 1 whenever search changes or view changes
    useEffect(() => { setPage(1); }, [debouncedSearch, view]);

    const fetchAllUsers = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(page),
                per_page: String(PER_PAGE),
            });
            if (debouncedSearch) params.set('search', debouncedSearch);
            const res = await authenticatedFetch(`/admin/users?${params.toString()}`);
            if (res.ok) {
                const data = await res.json();
                // Tolerate the old array-shaped response too
                const list = Array.isArray(data) ? data : (data.users || []);
                setUsers(list);
                setTotalPages(data.pages || 1);
                setTotalCount(data.total ?? list.length);
                // Admin count is for stats; re-compute from current page only
                // (it's a small inaccuracy for unsearched view but acceptable —
                // an exact figure would need a separate endpoint or full scan).
                setAdminCount(list.filter(u => u.is_system_admin).length);
            } else {
                setError('Failed to load admin data. Access denied.');
            }
        } catch (err) {
            setError('Error connecting to admin API.');
        } finally {
            setLoading(false);
        }
    }, [authenticatedFetch, page, debouncedSearch]);

    const fetchAllSocieties = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(page),
                per_page: String(PER_PAGE),
            });
            if (debouncedSearch) params.set('search', debouncedSearch);
            const res = await authenticatedFetch(`/admin/societies?${params.toString()}`);
            if (res.ok) {
                const data = await res.json();
                const list = Array.isArray(data) ? data : (data.societies || []);
                setSocieties(list);
                setTotalPages(data.pages || 1);
                setTotalCount(data.total ?? list.length);
            } else {
                console.error('Failed to load societies data.');
            }
        } catch (err) {
            console.error('Error connecting to admin API.', err);
        } finally {
            setLoading(false);
        }
    }, [authenticatedFetch, page, debouncedSearch]);

    const handleEditLink = async (membershipId, societyId, currentPlayerId) => {
        setEditingMembership({ id: membershipId, societyId, currentId: currentPlayerId });
        try {
            const res = await authenticatedFetch(`/admin/societies/${societyId}/players`);
            if (res.ok) {
                setSocietyPlayers(await res.json());
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleSaveLink = async (newPlayerId) => {
        setSavingLink(true);
        try {
            const res = await authenticatedFetch(`/admin/memberships/${editingMembership.id}/link-player`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ player_id: newPlayerId === '' ? null : parseInt(newPlayerId) })
            });
            if (res.ok) {
                setEditingMembership(null);
                fetchAllUsers();
            }
        } catch (err) {
            notify.error('Failed to update link');
        } finally {
            setSavingLink(false);
        }
    };

    const handleEditPlan = (user) => {
        setEditingPlan(user.id);
        setPlanForm({
            plan_type: user.plan_type || 'free',
            plan_expiry_date: user.plan_expiry_date ? user.plan_expiry_date.slice(0, 10) : ''
        });
    };

    const handleSavePlan = async () => {
        setSavingPlan(true);
        try {
            const body = {
                plan_type: planForm.plan_type,
                plan_source: 'manual',
                plan_expiry_date: planForm.plan_expiry_date ? new Date(planForm.plan_expiry_date).toISOString() : null
            };
            const res = await authenticatedFetch(`/admin/users/${editingPlan}/plan`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            if (res.ok) {
                setEditingPlan(null);
                fetchAllUsers();
            } else {
                const err = await res.json();
                notify.error(err.error || 'Failed to update plan');
            }
        } catch (err) {
            notify.error('Failed to update plan');
        } finally {
            setSavingPlan(false);
        }
    };

    useEffect(() => {
        if (isProfileLoading || !userProfile) return;
        if (!userProfile.is_system_admin) {
            navigate('/setup');
            return;
        }
        if (view === 'users') fetchAllUsers();
        else if (view === 'societies') fetchAllSocieties();
        // 'audit' fetches its own data inside AuditLogList
    }, [userProfile, isProfileLoading, navigate, fetchAllUsers, fetchAllSocieties, view]);

    const menuItems = [
        { name: 'Leaderboard', route: '/leaderboard', icon: <FiBarChart2 size={24} /> },
        { name: 'Rounds', route: '/tournaments/scorecard-entry', icon: <FiGrid size={24} /> },
        { name: 'Insights', route: '/insights', icon: <FiTrendingUp size={24} /> },
        { name: 'Manage', route: '/setup', icon: <FiSettings size={24} />, active: true },
    ];

    if (loading && users.length === 0 && societies.length === 0) {
        return (
            <div className="admin-page-container">
                <PageHeader title="Admin" subtitle="Platform" backTo="/setup" backLabel="Back to Manage" />
                <div style={{padding: '100px 20px', textAlign: 'center'}}>Loading system data...</div>
                <BottomNav menuItems={menuItems} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-page-container">
                <PageHeader title="Admin" subtitle="Platform" backTo="/setup" backLabel="Back to Manage" />
                <div style={{padding: '100px 20px', textAlign: 'center', color: 'red'}}>{error}</div>
                <BottomNav menuItems={menuItems} />
            </div>
        );
    }

    const formatDateTime = (iso) => {
        if (!iso) return 'Never';
        return new Date(iso).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
    };

    // Derive a human-meaningful subscription state from the user's plan fields.
    // The DB still stores plan_type as 'free' / 'pro' (an implementation detail);
    // these labels surface what that actually means in our paid-only model.
    const planStatus = (user) => {
        const isCurrentlyPro = (user.effective_plan || user.plan_type) === 'pro';
        if (isCurrentlyPro) {
            return user.plan_source === 'manual'
                ? { label: 'COMP',       cls: 'plan-pill-comp' }
                : { label: 'SUBSCRIBED', cls: 'plan-pill-subscribed' };
        }
        if (user.plan_expiry_date) {
            return { label: 'LAPSED', cls: 'plan-pill-lapsed' };
        }
        return { label: 'NEW', cls: 'plan-pill-new' };
    };

    // Human label for the RevenueCat period type captured on the user.
    const periodLabel = (pt) => {
        if (pt === 'trial') return 'In trial';
        if (pt === 'intro') return 'Intro offer';
        if (pt === 'normal') return 'Renewed';
        return pt;
    };
    // 'INITIAL_PURCHASE' -> 'Initial Purchase'
    const prettyEvent = (ev) =>
        ev ? ev.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '';

    return (
        <div className="admin-page-container">
            <PageHeader title="Admin" subtitle="Platform" backTo="/setup" backLabel="Back to Manage" />

            <div className="admin-content">
                <div className="view-toggle-container">
                    <button className={`view-toggle-btn ${view === 'users' ? 'active' : ''}`} onClick={() => setView('users')}>
                        <FiUsers /> Users
                    </button>
                    <button className={`view-toggle-btn ${view === 'societies' ? 'active' : ''}`} onClick={() => setView('societies')}>
                        <FiLayers /> Societies
                    </button>
                    <button className={`view-toggle-btn ${view === 'audit' ? 'active' : ''}`} onClick={() => setView('audit')}>
                        <FiActivity /> Audit
                    </button>
                    <button className={`view-toggle-btn ${view === 'insights' ? 'active' : ''}`} onClick={() => setView('insights')}>
                        <FiBarChart2 /> Insights
                    </button>
                </div>

                {view === 'audit' && <AuditLogList endpoint="/admin/audit-logs" showSociety />}

                {view === 'insights' && <AdminInsights />}

                {(view === 'users' || view === 'societies') && (
                <>
                <div className="admin-stats-row">
                    <div className="admin-stat-card">
                        {view === 'users' ? <FiUsers size={24} /> : <FiLayers size={24} />}
                        <div className="stat-info">
                            <span className="stat-value">{totalCount}</span>
                            <span className="stat-label">Total {view === 'users' ? 'Users' : 'Societies'}</span>
                        </div>
                    </div>
                    {view === 'users' && (
                        <div className="admin-stat-card">
                            <FiShield size={24} />
                            <div className="stat-info">
                                <span className="stat-value">{adminCount}</span>
                                <span className="stat-label">Admins (page)</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="admin-search-box">
                    <FiSearch className="search-icon" />
                    <input 
                        type="text" 
                        placeholder={view === 'users' ? "Search users..." : "Search societies..."} 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {view === 'users' ? (
                    <div className="users-list">
                        {users.map(user => (
                            <div key={user.id} className={`admin-user-card${user.is_deleted ? ' admin-card-muted' : ''}`}>
                                <div className="user-card-header">
                                    <div className="user-main-info">
                                        <FiUser size={20} className="user-icon" />
                                        <div>
                                            <h4>
                                                {user.is_deleted ? 'Deleted account' : (user.display_name || 'Anonymous')}
                                                {user.is_deleted && <span className="admin-pill admin-pill-deleted">DELETED</span>}
                                                {user.is_system_admin && <span className="admin-pill">SYS ADMIN</span>}
                                                {!user.is_deleted && (() => {
                                                    const s = planStatus(user);
                                                    return <span className={`plan-pill ${s.cls}`}>{s.label}</span>;
                                                })()}
                                            </h4>
                                            <p>{user.is_deleted ? 'Account anonymised' : user.email}</p>
                                        </div>
                                    </div>
                                    <div className="user-meta">
                                        <div className="meta-row">Joined: {formatDateTime(user.created_at)}</div>
                                        <div className="meta-row">Last Active: {formatDateTime(user.last_login_at)}</div>
                                    </div>
                                </div>

                                <div className="user-plan-section">
                                    {editingPlan === user.id ? (
                                        <div className="plan-editor">
                                            <select
                                                value={planForm.plan_type}
                                                onChange={(e) => setPlanForm({ ...planForm, plan_type: e.target.value })}
                                                disabled={savingPlan}
                                            >
                                                <option value="free">Free</option>
                                                <option value="pro">Pro</option>
                                            </select>
                                            <input
                                                type="date"
                                                placeholder="Expiry (optional)"
                                                value={planForm.plan_expiry_date}
                                                onChange={(e) => setPlanForm({ ...planForm, plan_expiry_date: e.target.value })}
                                                disabled={savingPlan}
                                            />
                                            <button className="edit-link-btn" onClick={handleSavePlan} disabled={savingPlan}>
                                                {savingPlan ? 'Saving...' : 'Save'}
                                            </button>
                                            <button className="link-cancel" onClick={() => setEditingPlan(null)}>Cancel</button>
                                        </div>
                                    ) : (
                                        <>
                                        <div className="plan-display">
                                            {user.subscription_period_type && (user.effective_plan || user.plan_type) === 'pro' && (
                                                <span className={`plan-source plan-period-${user.subscription_period_type}`}>
                                                    {periodLabel(user.subscription_period_type)}
                                                </span>
                                            )}
                                            {user.plan_expiry_date && (
                                                <span className={`plan-expiry ${new Date(user.plan_expiry_date) < new Date() ? 'expired' : ''}`}>
                                                    {new Date(user.plan_expiry_date) < new Date() ? 'Expired' : `Expires ${new Date(user.plan_expiry_date).toLocaleDateString()}`}
                                                </span>
                                            )}
                                            {user.plan_source && <span className="plan-source">{user.plan_source}</span>}
                                            <button className="edit-link-btn" onClick={() => handleEditPlan(user)}>Edit Plan</button>
                                        </div>
                                        {(user.subscription_purchased_at || user.subscription_last_event) && (
                                            <div className="plan-detail-rows">
                                                {user.subscription_purchased_at && (
                                                    <div className="plan-detail-row">
                                                        <span className="plan-detail-label">Subscribed</span>
                                                        <span>{formatDateTime(user.subscription_purchased_at)}</span>
                                                    </div>
                                                )}
                                                {user.subscription_last_event && (
                                                    <div className="plan-detail-row">
                                                        <span className="plan-detail-label">Last event</span>
                                                        <span>
                                                            {prettyEvent(user.subscription_last_event)}
                                                            {user.subscription_last_event_at ? ` · ${formatDateTime(user.subscription_last_event_at)}` : ''}
                                                        </span>
                                                    </div>
                                                )}
                                                {user.plan_expiry_date && (
                                                    <div className="plan-detail-row">
                                                        <span className="plan-detail-label">
                                                            {new Date(user.plan_expiry_date) < new Date() ? 'Expired' : 'Expires'}
                                                        </span>
                                                        <span>{formatDateTime(user.plan_expiry_date)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        </>
                                    )}
                                </div>

                                {!user.is_deleted && (
                                    <div className="user-activity-section">
                                        <button
                                            className="edit-link-btn"
                                            onClick={() => setTimelineFor(timelineFor === user.id ? null : user.id)}
                                            aria-expanded={timelineFor === user.id}
                                        >
                                            {timelineFor === user.id ? 'Hide activity' : 'Activity'}
                                        </button>
                                        {timelineFor === user.id && <UserTimeline userId={user.id} />}
                                    </div>
                                )}

                                <div className="user-societies-section">
                                    <h5>Societies ({user.societies?.length || 0})</h5>
                                    {user.societies && user.societies.length > 0 ? (
                                        <div className="mini-society-list">
                                            {user.societies.map(s => (
                                                <div key={s.id} className="mini-society-item">
                                                    <div className="s-name">
                                                        <strong>{s.name}</strong>
                                                        <span className={`s-role ${s.role.toLowerCase()}`}>{s.role}</span>
                                                    </div>
                                                    
                                                    <div className="link-player-box">
                                                        {editingMembership?.id === s.membership_id ? (
                                                            <div className="link-editor">
                                                                <select 
                                                                    defaultValue={s.linked_player?.id || ''} 
                                                                    onChange={(e) => handleSaveLink(e.target.value)}
                                                                    disabled={savingLink}
                                                                >
                                                                    <option value="">-- Unlinked --</option>
                                                                    {societyPlayers.map(p => (
                                                                        <option key={p.id} value={p.id}>{p.name} (Hcp: {p.handicap})</option>
                                                                    ))}
                                                                </select>
                                                                <button className="link-cancel" onClick={() => setEditingMembership(null)}>Cancel</button>
                                                            </div>
                                                        ) : (
                                                            <div className="s-player">
                                                                {s.linked_player ? (
                                                                    <>
                                                                        <FiInfo size={12} />
                                                                        <span>{s.linked_player.name}</span>
                                                                    </>
                                                                ) : (
                                                                    <span className="unlinked">No player linked</span>
                                                                )}
                                                                <button className="edit-link-btn" onClick={() => handleEditLink(s.membership_id, s.id, s.linked_player?.id)}>
                                                                    Edit Link
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="no-data-text">No society memberships found.</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="users-list">
                        {societies.map(society => (
                            <div key={society.id} className={`admin-user-card${society.is_archived ? ' admin-card-muted' : ''}`}>
                                <div className="user-card-header">
                                    <div className="user-main-info">
                                        <FiLayers size={20} className="user-icon" />
                                        <div>
                                            <h4>
                                                {society.name}
                                                <span className="admin-pill" style={{backgroundColor: '#0077be'}}>{society.invite_code}</span>
                                                {society.is_archived && <span className="admin-pill admin-pill-archived">ARCHIVED</span>}
                                            </h4>
                                            <p>Owner: {society.owner_email}</p>
                                        </div>
                                    </div>
                                    <div className="user-meta">
                                        <div className="meta-row">Created: {formatDateTime(society.created_at)}</div>
                                        <div className="meta-row">Members: {society.member_count}</div>
                                        {society.is_archived && <div className="meta-row">Archived: {formatDateTime(society.archived_at)}</div>}
                                    </div>
                                </div>
                                {/* Support access. An archived society is not
                                    offered — it is closed to its own members. */}
                                {!society.is_archived && (
                                    <div className="admin-society-actions">
                                        {String(adminSociety?.id) === String(society.id) ? (
                                            <>
                                                <span className="admin-open-badge">Open as admin</span>
                                                <button
                                                    className="admin-society-btn admin-society-btn--exit"
                                                    onClick={exitAdminSociety}
                                                >
                                                    <FiLogOut size={14} /> Close
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                className="admin-society-btn"
                                                onClick={() => openSociety(society)}
                                            >
                                                <FiLogIn size={14} /> Open as admin
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {totalPages > 1 && (
                    <div className="admin-pagination">
                        <button
                            className="page-btn"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page <= 1 || loading}
                        >
                            ← Prev
                        </button>
                        <span className="page-info">
                            Page {page} of {totalPages} · {totalCount} {view}
                        </span>
                        <button
                            className="page-btn"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages || loading}
                        >
                            Next →
                        </button>
                    </div>
                )}
                </>
                )}
            </div>
            <BottomNav menuItems={menuItems} />
        </div>
    );
};

export default AdminDashboard;